import { Checkbox, IconButton } from "@mui/material"
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from "@mui-symbols-material/w300"
import type { ColumnDef } from "@tanstack/react-table"

import { InventoryProductDescription, InventoryProductIdWithVersion } from "../../../components"
import { TransferFormProductRowActions } from "../components/TransferFormProducts/TransferFormProductRowActions"
import type { TransferFormProductInventory } from "../types"
import { calculateProductQtyToMove } from "../utils/transfer"

import { Button } from "@/components/common/Button"
import { SELECT_COLUMN_SIZE } from "@/constants/common"
import type { InventoryByProductItem } from "@/graphql/codegen/graphql"
import { formatNumber } from "@/utils/number"

const createBaseColumns = <TRow extends InventoryByProductItem>(qtyOnHandHeader: string): ColumnDef<TRow>[] => [
  {
    accessorKey: "productId",
    header: "Product ID",
    cell: ({ row }) => <InventoryProductIdWithVersion product={row.original.product} />,
    enableSorting: false,
  },
  {
    accessorKey: "description",
    header: "Description",
    cell: ({ row }) => <InventoryProductDescription product={row.original.product} />,
    enableSorting: false,
  },
  {
    accessorKey: "supplier",
    header: "Supplier",
    cell: ({ row }) => row.original.product.company?.name ?? "",
    enableSorting: false,
  },
  {
    accessorKey: "qtyOnHand",
    header: qtyOnHandHeader,
    cell: ({ row }) =>
      `${formatNumber(row.original.qtyOnHand)} ${row.original.product.packageUsWeightUomDetails?.name}`,
    enableSorting: false,
  },
]

export const useTransferProductSelectionColumns = (): ColumnDef<InventoryByProductItem>[] => [
  {
    id: "select",
    header: "",
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onChange={row.getToggleSelectedHandler()}
        disabled={!row.getCanSelect()}
      />
    ),
    size: SELECT_COLUMN_SIZE,
    enableSorting: false,
  },
  ...createBaseColumns<InventoryByProductItem>("Total QoH"),
]

interface UseTransferFormProductColumnsProps {
  onProductRemove: (productId: number) => void
  onPalletsSelect: (productId: number) => void
  readOnly?: boolean
}

export const useTransferFormProductColumns = ({
  onProductRemove,
  onPalletsSelect,
  readOnly = false,
}: UseTransferFormProductColumnsProps): ColumnDef<TransferFormProductInventory>[] => [
  ...createBaseColumns<TransferFormProductInventory>("Qty on Hand"),
  {
    accessorKey: "totalQtyToMove",
    header: "Total Qty to Move",
    cell: ({ row }) => {
      if (!row.original.pallets.length) return ""

      const quantity = calculateProductQtyToMove(row.original)
      return `${formatNumber(quantity)} ${row.original.product.packageUsWeightUomDetails?.name ?? ""}`
    },
    enableSorting: false,
  },
  {
    accessorKey: "pallets",
    header: "Pallets",
    cell: ({ row }) => {
      const palletCount = row.original.pallets.length

      return palletCount ? (
        <span data-testid='transfer.form-products-table.pallets-count'>{palletCount} Selected</span>
      ) : (
        <Button
          appearance='outlined'
          disabled={readOnly}
          onClick={() => onPalletsSelect(row.original.product.productId)}
          data-testid='transfer.form-products-table.select-pallets-button'
        >
          Select Pallets
        </Button>
      )
    },
    enableSorting: false,
  },
  {
    accessorKey: "actions",
    header: "",
    size: 112,
    enableSorting: false,
    cell: ({ row }) => (
      <div className='mt-[-8px] flex items-center justify-end gap-x-2'>
        <TransferFormProductRowActions
          productId={row.original.product.productId}
          hasPallets={!!row.original.pallets.length}
          readOnly={readOnly}
          onProductRemove={onProductRemove}
          onPalletsSelect={onPalletsSelect}
        />
        <IconButton size='small' onClick={row.getToggleExpandedHandler()}>
          {row.getIsExpanded() ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
        </IconButton>
      </div>
    ),
  },
]
